import { z } from "zod";
import { callLLM } from "../llm/utils";

const prompt = `Here is the use case for the social media post you will be generating:
<use_case>
{{USE_CASE}}
</use_case>

The user has provided the following high-level points they would like you to cover in the post:
<user_provided_high_level_points>
{{USER_PROVIDED_HIGH_LEVEL_POINTS}}
</user_provided_high_level_points>

The desired tone for the post is:
<tone>{{TONE}}</tone>

<old_content_instructions>
If there is any previously generated content for this post, it will be provided below. If so, make sure your newly generated content is completely distinct and does not repeat any exact phrasing.
<old_content>
{{OLD_GENERATED_CONTENT}}
</old_content>
</old_content_instructions>

Before generating the {{USE_CASE}}, carefully review the user-provided input, desired tone, and old generated content (if provided).

The {{USE_CASE}} you generate should meet the following criteria:
- Be concise, engaging, eye-catchy and attention-grabbing
- Effectively communicate the key points provided by the user
- Address the specific use case
- Match the desired tone
- If an <old_content> was provided, ensure the new content is completely distinct, fresh and unique. Do not repeat any exact phrasing.

<brainstorm>
Jot down some key ideas and phrases to potentially include in the {{USE_CASE}} based on the user-provided points and use case.
Think about how to make the {{USE_CASE}} concise yet engaging and attention-grabbing.

When crafting your response, two important aspects are "perplexity" and "burstiness." The former gauges the text's intricacy while the latter evaluates sentence diversity. Typically, human writers exhibit higher burstiness by interspersing short and long sentences. In contrast, AI-generated sentences tend to be uniform in length. To ensure the response you generate for me has a desirable level of perplexity and burstiness, please keep this in mind.
When also creating written response, artificial intelligence tends to utilize phrasing that is different from what a human would choose. Utilize uncommon terminology to enhance the originality of the piece.

These are the settings for your response:

Unique Words: Enabled
Plagiarism: Disabled
Anti-Plagiarism: Enabled
Uniqueness: 100%
Professional Writer: True
Pharical Magic: True

Please use this settings to your response, and the more uniqueness the more you'll re-write the response with unique words.
Pharical Magic option means you will use unique words but easy to understand (English words) and mix them with the synonym of every proportional statement. Or vice-versa. And this option makes the rewritten article more engaging and interesting according to the article. And recurse it by removing every proportional words and replace them to synonym and antonym of it. Replace statements to simile too.

Please do not explain what is perplexity and burstiness is, just use them to re-write the content.

</brainstorm>

When you have generated a {{USE_CASE}} that meets all the above criteria, provide it inside <r> tags.

<translation>
Once you have crafted your email, translate it into this language: <language>{{LANGUAGE}}</language> only if the <response_return_language> is not "English".
</translation>


The response should be in the plain text. Don't write in the markdown format.
The response should be in the translated language if the user requested language is not "English". Do not use double quotes before and after the response.
The response should be inside <ideas> and </ideas> without any additional details, hint, note, or explanation. Don't forgot to close the tags.
For the list, do not use number or '-' at the beginning. Separate by the new line.

<example>
   <ideas>Generated Content without additional information</ideas>
 </example>
`;

export const BlogGeneratorFormSchema = z.object({
  tone: z.object({
    id: z.string().min(1, "Please select a tone"),
    value: z.string(),
  }),
  language: z.object({
    id: z.string().min(1, "Please select a language"),
    value: z.string(),
  }),
  content: z.string().min(10, "Topics must be at least 10 characters"),
});

export type IBlogGeneratorFormSchema = z.infer<typeof BlogGeneratorFormSchema>;

export interface GenerateAIBlogProps {
  info: IBlogGeneratorFormSchema;
  useCase: string;
  old: string;
}

function extractResult(content: string): string | null {
  // Regular expression to match content within <email_draft> tags
  const regex = /<ideas>([\s\S]*?)<\/ideas>/;

  // Use regex.exec to find matches
  const match = regex.exec(content);

  // If a match is found, return the content within the tags, otherwise return null
  // @ts-ignore
  return match ? match?.[1].trim() : null;
}

export const generateAIBlog = async ({ info, useCase, old }: GenerateAIBlogProps) => {
  const promptInput = {
    TONE: info.tone?.id,
    USER_PROVIDED_HIGH_LEVEL_POINTS: info.content,
    USE_CASE: useCase,
    LANGUAGE: info.language?.id,
    OLD_GENERATED_EMAIL: old,
  };
  const result = await callLLM(prompt, promptInput);
  console.log(result);
  if (result) {
    // take only content inside <bio></bio> tags
    return extractResult(result);
  }
};
